import { useDrawingStore } from '../stores/drawingStore';
import { useToolStore } from '../stores/toolStore';
import { Icon } from './Icon';

const COLOR_SWATCHES = [
  { color: 0xd4af37, label: 'Gold' },
  { color: 0xdc2626, label: 'Red' },
  { color: 0x3b82f6, label: 'Blue' },
  { color: 0x22c55e, label: 'Green' },
  { color: 0xffffff, label: 'White' },
  { color: 0x888888, label: 'Grey' },
  { color: 0xf97316, label: 'Orange' },
  { color: 0x000000, label: 'Black' },
];

const WIDTH_OPTIONS = [
  { value: 2, label: 'Thin' },
  { value: 4, label: 'Medium' },
  { value: 8, label: 'Thick' },
];

export function DrawToolBar() {
  const strokes = useDrawingStore((s) => s.strokes);
  const currentColor = useDrawingStore((s) => s.color);
  const currentWidth = useDrawingStore((s) => s.strokeWidth);
  const setColor = useDrawingStore((s) => s.setColor);
  const setStrokeWidth = useDrawingStore((s) => s.setStrokeWidth);
  const undoLast = useDrawingStore((s) => s.undoLast);
  const clearAll = useDrawingStore((s) => s.clearAll);
  const activeTool = useToolStore((s) => s.activeTool);

  const visible = activeTool === 'draw' || strokes.length > 0;
  if (!visible) return null;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        backgroundColor: 'rgba(10, 8, 12, 0.92)',
        border: '1px solid rgba(212, 175, 55, 0.3)',
        borderRadius: 8,
        padding: '6px 12px',
        fontFamily: 'Georgia, serif',
      }}
    >
      {/* Color swatches */}
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        {COLOR_SWATCHES.map((swatch) => {
          const hex = '#' + swatch.color.toString(16).padStart(6, '0');
          const isActive = currentColor === swatch.color;
          return (
            <button
              key={swatch.color}
              onClick={() => setColor(swatch.color)}
              title={swatch.label}
              style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                backgroundColor: hex,
                border: isActive
                  ? '2px solid #d4af37'
                  : swatch.color === 0x000000
                    ? '1px solid #555'
                    : '1px solid rgba(255,255,255,0.2)',
                cursor: 'pointer',
                padding: 0,
                transform: isActive ? 'scale(1.2)' : 'scale(1)',
                transition: 'transform 0.1s ease',
              }}
            />
          );
        })}
      </div>

      {/* Separator */}
      <div style={{ width: 1, height: 20, backgroundColor: 'rgba(212, 175, 55, 0.2)' }} />

      {/* Width options */}
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        {WIDTH_OPTIONS.map((opt) => {
          const isActive = currentWidth === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => setStrokeWidth(opt.value)}
              title={opt.label}
              style={{
                width: 28,
                height: 24,
                borderRadius: 4,
                border: 'none',
                backgroundColor: isActive ? 'rgba(212, 175, 55, 0.2)' : 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              <div
                style={{
                  width: 16,
                  height: opt.value,
                  backgroundColor: isActive ? '#d4af37' : '#888',
                  borderRadius: opt.value / 2,
                }}
              />
            </button>
          );
        })}
      </div>

      {/* Separator */}
      <div style={{ width: 1, height: 20, backgroundColor: 'rgba(212, 175, 55, 0.2)' }} />

      {/* Undo */}
      <button
        onClick={undoLast}
        disabled={strokes.length === 0}
        title="Undo last stroke"
        style={{
          width: 28,
          height: 28,
          borderRadius: 4,
          border: 'none',
          backgroundColor: 'transparent',
          color: strokes.length > 0 ? '#c0c0c0' : '#555',
          cursor: strokes.length > 0 ? 'pointer' : 'not-allowed',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
      >
        <Icon name="refresh-cw" size={16} />
      </button>

      {/* Clear all */}
      <button
        onClick={clearAll}
        disabled={strokes.length === 0}
        title="Clear all drawings"
        style={{
          width: 28,
          height: 28,
          borderRadius: 4,
          border: 'none',
          backgroundColor: 'transparent',
          color: strokes.length > 0 ? '#c0c0c0' : '#555',
          cursor: strokes.length > 0 ? 'pointer' : 'not-allowed',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
      >
        <Icon name="trash" size={16} />
      </button>

      {/* Stroke count */}
      {strokes.length > 0 && (
        <span style={{ fontSize: 10, color: '#8b6914', fontStyle: 'italic', whiteSpace: 'nowrap' }}>
          {strokes.length} stroke{strokes.length !== 1 ? 's' : ''}
        </span>
      )}
    </div>
  );
}
